import { FeatureGroupEnum, TEST_HUB_URL } from '@/constants/constants';
import { apiFetch } from '@/utils/apiWrapper';

// Base URL for features endpoints
const featuresUrl = `${TEST_HUB_URL}subscriptions/features/`;
const featureGroupsUrl = `${TEST_HUB_URL}subscriptions/feature-groups/`;
const subOperationsUrl = `${TEST_HUB_URL}subscriptions/subscription-operations/`;

// Types for Feature data

export enum FeatureType {
  BOOLEAN = 'boolean',
  STRING = 'string',
  NUMBER = 'number',
  ARRAY = 'array',
  OBJECT = 'object',
}
export interface Feature {
  _id: string;
  name: string;
  value: string;
  type: FeatureType;
  description: string;
  featureGroupId: string;
  guid: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FeatureGroup {
  _id: string;
  name: string;
  description?: string;
  guid: FeatureGroupEnum;
  createdAt?: string;
  updatedAt?: string;
  features: Feature[];
}

// Create feature DTO
export interface CreateFeatureDTO {
  name: string;
  value: string;
  description: string;
  featureGroupId: string;
  guid?: string;
}

// Update feature DTO
export interface UpdateFeatureDTO {
  name?: string;
  value?: string;
  description?: string;
  featureGroupId?: string;
}

// Create feature group DTO
export interface CreateFeatureGroupDTO {
  name: string;
  description?: string;
  guid?: string;
}

// Update feature group DTO
export interface UpdateFeatureGroupDTO {
  name?: string;
  description?: string;
}

// Feature filters
export interface FeatureFilters {
  featureGroupId?: string;
}

/**
 * Fetches all features with optional filters
 * @param filters Optional filters for the features
 * @returns Array of features
 */
export async function getAllFeatures(filters?: FeatureFilters): Promise<Feature[]> {
  try {
    let url = `${featuresUrl}`;

    // Add query parameters if filters provided
    if (filters) {
      const queryParams = new URLSearchParams();

      if (filters.featureGroupId) {
        queryParams.append('featureGroupId', filters.featureGroupId);
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await apiFetch(url);

    if (!response) {
      throw new Error('Error fetching features');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching features:', error);
    throw error;
  }
}

/**
 * Fetches a feature by ID
 * @param id Feature ID
 * @returns Feature data
 */
export async function getFeatureById(id: string): Promise<Feature> {
  try {
    const url = `${featuresUrl}${id}`;

    const response = await apiFetch(url);

    if (!response) {
      throw new Error(`Could not fetch feature with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching feature with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Creates a new feature
 * @param featureData Feature data to create
 * @returns The created feature
 */
export async function createFeature(featureData: CreateFeatureDTO): Promise<Feature> {
  try {
    const response = await apiFetch(featuresUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(featureData),
    });

    if (!response) {
      throw new Error('Error creating feature');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating feature:', error);
    throw error;
  }
}

/**
 * Updates a feature
 * @param id Feature ID to update
 * @param featureData Updated feature data
 * @returns The updated feature
 */
export async function updateFeature(id: string, featureData: UpdateFeatureDTO): Promise<Feature> {
  try {
    const url = `${featuresUrl}${id}`;

    const response = await apiFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(featureData),
    });

    if (!response) {
      throw new Error(`Could not update feature with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error updating feature with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Deletes a feature
 * @param id Feature ID to delete
 * @returns Success message
 */
export async function deleteFeature(id: string): Promise<{ success: boolean; message: string }> {
  try {
    const url = `${featuresUrl}${id}`;

    const response = await apiFetch(url, {
      method: 'DELETE',
    });

    if (!response) {
      throw new Error(`Could not delete feature with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error deleting feature with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Fetches all feature groups
 * @returns Array of feature groups
 */
export async function getAllFeatureGroupsWithFeatures(
  organizationId: string,
): Promise<FeatureGroup[]> {
  try {
    const response = await apiFetch(
      `${subOperationsUrl}feature-group-with-features/${organizationId}`,
    );

    if (!response) {
      throw new Error('Error fetching feature groups');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching feature groups:', error);
    throw error;
  }
}

/**
 * Fetches a feature group by ID
 * @param id Feature group ID
 * @returns Feature group data
 */
export async function getFeatureGroupById(id: string): Promise<FeatureGroup> {
  try {
    const url = `${featureGroupsUrl}${id}`;

    const response = await apiFetch(url);

    if (!response) {
      throw new Error(`Could not fetch feature group with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching feature group with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Creates a new feature group
 * @param groupData Feature group data to create
 * @returns The created feature group
 */
export async function createFeatureGroup(groupData: CreateFeatureGroupDTO): Promise<FeatureGroup> {
  try {
    const response = await apiFetch(featureGroupsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(groupData),
    });

    if (!response) {
      throw new Error('Error creating feature group');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating feature group:', error);
    throw error;
  }
}

/**
 * Updates a feature group
 * @param id Feature group ID to update
 * @param groupData Updated feature group data
 * @returns The updated feature group
 */
export async function updateFeatureGroup(
  id: string,
  groupData: UpdateFeatureGroupDTO,
): Promise<FeatureGroup> {
  try {
    const url = `${featureGroupsUrl}${id}`;

    const response = await apiFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(groupData),
    });

    if (!response) {
      throw new Error(`Could not update feature group with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error updating feature group with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Deletes a feature group
 * @param id Feature group ID to delete
 * @returns Success message
 */
export async function deleteFeatureGroup(
  id: string,
): Promise<{ success: boolean; message: string }> {
  try {
    const url = `${featureGroupsUrl}${id}`;

    const response = await apiFetch(url, {
      method: 'DELETE',
    });

    if (!response) {
      throw new Error(`Could not delete feature group with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error deleting feature group with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Fetches features by feature group ID
 * @param featureGroupId Feature group ID
 * @returns Array of features
 */
export async function getFeaturesByGroupId(featureGroupId: string): Promise<Feature[]> {
  return getAllFeatures({ featureGroupId });
}
